import { useEffect, useState } from "react";
import { MeterGroup } from "primereact/metergroup";
import Tooltip from "../../../../utils/element-generator/Tooltip";
import { motion, AnimatePresence } from "framer-motion";
import { Skeleton } from "primereact/skeleton";
import { useParams } from "react-router-dom";
import { useAPIgetPackageByBC } from "../api/api-get-package-by-bc";

// Skeleton card for loading state
const SkeletonCard = () => (
  <div className="elevated-card-tight-vertical flex-1 min-h-[120px] h-[120px] !shadow-md border border-gray-200 !p-3 flex flex-col justify-between">
    <div className="row-between-center gap-5 items-center h-full">
      <div className="col-center-start gap-2 w-full">
        <Skeleton width="7rem" height="1rem" className="mb-2" />
        <Skeleton width="40%" height="1.25rem" />
      </div>
      <Skeleton shape="circle" className="!w-[2rem] !h-[2rem] flex-shrink-0" />
    </div>
  </div>
);

const RemainingCustomerPackageDetailByBC = (props) => {
  const { onDataReady, id, product_name } = props;
  const { data, loading, error, fetchData, filterValue, setFilterValue } =
    useAPIgetPackageByBC();

  const [values, setValues] = useState([]);
  const [maxValue, setMaxValue] = useState(0);

  const accessibleColors = [
    "#1b9e77",
    "#d95f02",
    "#7570b3",
    "#e7298a",
    "#66a61e",
    "#e6ab02",
    "#a6761d",
    "#666666",
  ];

  useEffect(() => {
    fetchData({ id: id });
  }, [id]);

  useEffect(() => {
    if (data) {
      const newMeterGroup = data.items.map((item, index) => ({
        label: item.bc_name,
        value: item.total,
        color: accessibleColors[index % accessibleColors.length],
        icon: "pi pi-user",
      }));

      const BCList = data.rawData.map((item) => ({
        label: item.bc_name,
        value: item.bc_id,
      }));

      BCList.push({ label: "All", value: 0 });

      onDataReady(BCList);
      setMaxValue(data.max);
      setValues(newMeterGroup);
    }
  }, [data]);

  const getPercentage = (value, total) => {
    if (total === 0) return "0%";
    return ((value / total) * 100).toFixed(1) + "%";
  };

  const labelList = ({ values }) => (
    <div className="row-around-center flex-wrap gap-4 mb-4">
      {values.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="elevated-card-tight-vertical flex-1 min-h-[120px] h-[120px] !shadow-md border border-gray-300 !p-3 flex flex-col justify-between"
        >
          <div className="row-between-center gap-5 items-center h-full">
            <div className="col-center-start gap-1">
              {item.label !== "Previous BC" ? (
                <span className="font-semibold text-sm truncate max-w-[100%]">
                  {item.label} ({getPercentage(item.value, maxValue)})
                </span>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-sm truncate max-w-[100%]">
                    {item.label} ({getPercentage(item.value, maxValue)})
                  </span>
                  <Tooltip text="Packages from retired BCs" />
                </div>
              )}
              <span className="font-bold text-md whitespace-nowrap">
                {item.value} pax
              </span>
            </div>
            <span
              className="w-8 aspect-square row-center-center rounded-full shrink-0"
              style={{ backgroundColor: item.color, color: "#ffffff" }}
            >
              <i className={item.icon} />
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const start = () => (
    <div className="row-between-center mt-3 mb-2 relative">
      <span className="font-bold text-sm">Total Packages</span>
      <span className="font-bold text-sm">{maxValue} pax</span>
    </div>
  );

  return (
    <div className="elevated-card-tight-vertical !shadow-md border border-gray-300 w-full">
      <div className="flex flex-col items-start gap-1 mb-4">
        <label className="flex items-center gap-2 text-lg font-semibold text-black">
          <i className="pi pi-users text-green-500 text-lg"></i>
          Package Sales By BC
        </label>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full col-center-start gap-4"
          >
            <div className="row-around-center flex-wrap gap-4 w-full">
              {[...Array(4)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
            <div className="col-center-start gap-2 w-full">
              <div className="row-between-center mb-2 w-full">
                <Skeleton width="5rem" height="1rem" />
                <Skeleton width="4rem" height="1rem" />
              </div>
              <Skeleton width="100%" height="0.75rem" />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MeterGroup
              labelPosition="start"
              start={start}
              values={values}
              max={maxValue}
              labelList={labelList}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RemainingCustomerPackageDetailByBC;
